import * as Yup from 'yup';
import {
  REQUIRED_ERROR_MSG,
  POSITIVE_NUMBER_ERROR_MSG
} from 'utils/constants/errors';
import { mapping as heightMapping } from 'features/clientProfile/components/assessmentTab/AssessmentForm/form/HeightField';
import { mapping as bodyFatPercentageMapping } from 'features/clientProfile/components/assessmentTab/AssessmentForm/form/BodyFatPercentageField';

const progressValidation = {
  url: Yup.string()
};

const ratingValidation = {
  rating_measurement_unit: Yup.number()
    .integer()
    .positive()
    .max(5)
    .required(REQUIRED_ERROR_MSG),
  dysfunction: Yup.lazy(obj =>
    Yup.object(
      Object.fromEntries(
        Object.entries(obj).map(([key]) => [key, Yup.boolean()])
      )
    ).when('rating_measurement_unit', {
      is: val => Number(val) < 5,
      then: Yup.object().test('atLeastOneCheckedTest', null, function test(
        obj
      ) {
        if (Object.values(obj).filter(v => v === true).length) {
          return true; // everything is fine
        }

        return new Yup.ValidationError(
          'Please choose at least one option',
          null,
          this.path
        );
      })
    })
  )
};

export const addAssessmentSchema = Yup.object().shape({
  assessment_type: Yup.string().required(REQUIRED_ERROR_MSG),
  gender: Yup.string().required(REQUIRED_ERROR_MSG),

  birthdate: Yup.object().shape({
    month: Yup.number()
      .integer()
      .min(1)
      .max(12)
      .required('month field is required!'),
    day: Yup.number()
      .integer()
      .min(1)
      .required('day field is required!')
      .max(31),
    year: Yup.number()
      .integer()
      .min(0)
      .required('year field is required!')
  }),

  weight: Yup.object().shape({
    value: Yup.number()
      .positive(POSITIVE_NUMBER_ERROR_MSG)
      .required(REQUIRED_ERROR_MSG),
    measurement_unit: Yup.string().required(REQUIRED_ERROR_MSG)
  }),

  height: Yup.object().shape({
    unit: Yup.string().required(REQUIRED_ERROR_MSG),
    cm: Yup.number().when('unit', {
      is: heightMapping.metricUnit.value,
      then: Yup.number()
        .positive(POSITIVE_NUMBER_ERROR_MSG)
        .required(REQUIRED_ERROR_MSG)
    }),
    feet: Yup.number().when('unit', {
      is: heightMapping.imperialUnit.value,
      then: Yup.number()
        .positive(POSITIVE_NUMBER_ERROR_MSG)
        .required(REQUIRED_ERROR_MSG)
    }),
    inch: Yup.number().when('unit', {
      is: heightMapping.imperialUnit.value,
      then: Yup.number()
        .integer()
        .min(0)
        .max(11)
        .required(REQUIRED_ERROR_MSG)
    })
  }),

  body_fat_percentage: Yup.object().shape({
    method: Yup.string().required(REQUIRED_ERROR_MSG),
    caliperMethod: Yup.string().when('method', {
      is: bodyFatPercentageMapping.caliperMethod.value,
      then: Yup.string().required(REQUIRED_ERROR_MSG)
    }),
    textFatPercentage: Yup.number().when('method', {
      is: bodyFatPercentageMapping.textMethod.value,
      then: Yup.number()
        .max(100)
        .positive(POSITIVE_NUMBER_ERROR_MSG)
        .required(REQUIRED_ERROR_MSG)
    }),
    chest: Yup.number().when('method', {
      is: bodyFatPercentageMapping.caliperMethod.value,
      then: Yup.number()
        .positive(POSITIVE_NUMBER_ERROR_MSG)
        .required(REQUIRED_ERROR_MSG)
    }),
    abdominal: Yup.number().when('method', {
      is: bodyFatPercentageMapping.caliperMethod.value,
      then: Yup.number()
        .positive(POSITIVE_NUMBER_ERROR_MSG)
        .required(REQUIRED_ERROR_MSG)
    }),
    thigh: Yup.number().when('method', {
      is: bodyFatPercentageMapping.caliperMethod.value,
      then: Yup.number()
        .positive(POSITIVE_NUMBER_ERROR_MSG)
        .required(REQUIRED_ERROR_MSG)
    }),
    subscapular: Yup.number().when(
      ['method', 'caliperMethod'],
      (method, caliperMethod, schema) => {
        return method === bodyFatPercentageMapping.caliperMethod.value &&
          (caliperMethod === bodyFatPercentageMapping.caliperMethod.jp7.value ||
            caliperMethod ===
              bodyFatPercentageMapping.caliperMethod.parillo9.value)
          ? schema
              .positive(POSITIVE_NUMBER_ERROR_MSG)
              .required(REQUIRED_ERROR_MSG)
          : schema;
      }
    ),
    tricep: Yup.number().when(
      ['method', 'caliperMethod'],
      (method, caliperMethod, schema) => {
        return method === bodyFatPercentageMapping.caliperMethod.value &&
          (caliperMethod === bodyFatPercentageMapping.caliperMethod.jp7.value ||
            caliperMethod ===
              bodyFatPercentageMapping.caliperMethod.parillo9.value)
          ? schema
              .positive(POSITIVE_NUMBER_ERROR_MSG)
              .required(REQUIRED_ERROR_MSG)
          : schema;
      }
    ),
    suprailiac: Yup.number().when(
      ['method', 'caliperMethod'],
      (method, caliperMethod, schema) => {
        return method === bodyFatPercentageMapping.caliperMethod.value &&
          (caliperMethod === bodyFatPercentageMapping.caliperMethod.jp7.value ||
            caliperMethod ===
              bodyFatPercentageMapping.caliperMethod.parillo9.value)
          ? schema
              .positive(POSITIVE_NUMBER_ERROR_MSG)
              .required(REQUIRED_ERROR_MSG)
          : schema;
      }
    ),
    midaxillary: Yup.number().when(
      ['method', 'caliperMethod'],
      (method, caliperMethod, schema) => {
        return method === bodyFatPercentageMapping.caliperMethod.value &&
          caliperMethod === bodyFatPercentageMapping.caliperMethod.jp7.value
          ? schema
              .positive(POSITIVE_NUMBER_ERROR_MSG)
              .required(REQUIRED_ERROR_MSG)
          : schema;
      }
    ),
    lower_back: Yup.number().when(
      ['method', 'caliperMethod'],
      (method, caliperMethod, schema) => {
        return method === bodyFatPercentageMapping.caliperMethod.value &&
          caliperMethod ===
            bodyFatPercentageMapping.caliperMethod.parillo9.value
          ? schema
              .positive(POSITIVE_NUMBER_ERROR_MSG)
              .required(REQUIRED_ERROR_MSG)
          : schema;
      }
    ),
    calf: Yup.number().when(
      ['method', 'caliperMethod'],
      (method, caliperMethod, schema) => {
        return method === bodyFatPercentageMapping.caliperMethod.value &&
          caliperMethod ===
            bodyFatPercentageMapping.caliperMethod.parillo9.value
          ? schema
              .positive(POSITIVE_NUMBER_ERROR_MSG)
              .required(REQUIRED_ERROR_MSG)
          : schema;
      }
    ),
    bicep: Yup.number().when(
      ['method', 'caliperMethod'],
      (method, caliperMethod, schema) => {
        return method === bodyFatPercentageMapping.caliperMethod.value &&
          caliperMethod ===
            bodyFatPercentageMapping.caliperMethod.parillo9.value
          ? schema
              .positive(POSITIVE_NUMBER_ERROR_MSG)
              .required(REQUIRED_ERROR_MSG)
          : schema;
      }
    )
  }),

  measurements: Yup.object().shape({
    chest: Yup.number()
      .positive(POSITIVE_NUMBER_ERROR_MSG)
      .required(REQUIRED_ERROR_MSG),
    waist: Yup.number()
      .positive(POSITIVE_NUMBER_ERROR_MSG)
      .required(REQUIRED_ERROR_MSG),
    hips: Yup.number()
      .positive(POSITIVE_NUMBER_ERROR_MSG)
      .required(REQUIRED_ERROR_MSG),
    upper_leg: Yup.number()
      .positive(POSITIVE_NUMBER_ERROR_MSG)
      .required(REQUIRED_ERROR_MSG),
    calf: Yup.number()
      .positive(POSITIVE_NUMBER_ERROR_MSG)
      .required(REQUIRED_ERROR_MSG),
    upper_arm: Yup.number()
      .positive(POSITIVE_NUMBER_ERROR_MSG)
      .required(REQUIRED_ERROR_MSG)
  }),

  dowel_bodyweight_squat: Yup.object().when('assessment_type', {
    is: 'First Session',
    then: Yup.object().shape(ratingValidation)
  }),
  dowel_standing_shoulder_press: Yup.object().when('assessment_type', {
    is: 'First Session',
    then: Yup.object().shape(ratingValidation)
  }),
  dowel_standing_hip_hinge: Yup.object().when('assessment_type', {
    is: 'First Session',
    then: Yup.object().shape(ratingValidation)
  }),
  db_front_foot_elevated_split_squat: Yup.object().when('assessment_type', {
    is: 'First Session',
    then: Yup.object().shape(ratingValidation)
  }),
  ng_shoulder_width_lat_pull_down: Yup.object().when('assessment_type', {
    is: 'First Session',
    then: Yup.object().shape(ratingValidation)
  }),

  progress_photos: Yup.object().shape({
    front: Yup.object().shape(progressValidation),
    side: Yup.object().shape(progressValidation),
    back: Yup.object().shape(progressValidation)
  })
});
